import type { ApolloClient } from '@apollo/client';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useApolloClient } from '@apollo/client';
import { isNil } from 'es-toolkit';
import PropTypes from 'prop-types';

import type { AuthContextValue } from '@savvy/deprecated-shared/contexts/jwt-context.context';
import { USER_CONFIRMATION_STATUS_PENDING_EMAIL_CONFIRMATION } from '@savvy/constants/user-confirmation-statuses';
import { USER_PROFILE_CLIENT } from '@savvy/constants/user-profiles';
import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';
import { getClientStageRedirectRoute, LOGIN_CONTEXT_PARAM } from '@savvy/deprecated-shared/utils/routing';
import { clearUserInfo, isExpiredJWT } from '@savvy/deprecated-shared/utils/savvy-auth';

type AuthGuardProps = {
  children: ReactNode;
  readonly forAdmin?: boolean;
  /** Like forAdmin, but also allows CSA — scope to pages a CSA genuinely needs, not every admin page. */
  readonly forAdminOrCsa?: boolean;
  readonly forAdvisor?: boolean;
  readonly forClient?: boolean;
  readonly forNonAdvisoryUser?: boolean;
};

type CheckAuthProps = {
  jwtToken: string | null;
  apolloClient: ApolloClient<object>;
  auth: AuthContextValue;
  router: ReturnType<typeof useRouter>;
  pathname: string;
  searchParams: ReturnType<typeof useSearchParams>;
  setChecked: (checked: boolean) => void;
  forAdmin?: boolean;
  forAdminOrCsa?: boolean;
  forAdvisor?: boolean;
  forClient?: boolean;
  forNonAdvisoryUser?: boolean;
  isSmallScreen: boolean;
};

export function loginRedirectQuery(pathname: string, extraParams: Record<string, string> = {}) {
  const searchString = window.location.search;
  const searchParams = new URLSearchParams(searchString);

  // Only relevant for login page, TODO: consolidate on approach here
  const loginContext = searchParams.get('login_context');
  searchParams.delete('login_context');
  const loginToken = searchParams.get(LOGIN_CONTEXT_PARAM);
  searchParams.delete(LOGIN_CONTEXT_PARAM);

  for (const [key, value] of Object.entries(extraParams)) {
    if (value) {
      searchParams.set(key, value);
    }
  }

  type AuthGuardUrlQuery = {
    returnUrl: string;
    login_context?: string;
    [LOGIN_CONTEXT_PARAM]?: string;
  };
  // TODO: use router to improve this logic
  const query: AuthGuardUrlQuery = {
    returnUrl: `${pathname}${Array.from(searchParams.keys()).length > 0 ? `?${searchParams.toString()}` : ''}`,
  };

  if (loginContext) {
    query.login_context = loginContext;
  }
  if (loginToken) {
    query[LOGIN_CONTEXT_PARAM] = loginToken;
  }

  return query;
}

export function checkAuth({
  jwtToken,
  apolloClient,
  auth,
  router,
  pathname,
  searchParams,
  setChecked,
  forAdmin,
  forAdminOrCsa,
  forAdvisor,
  forClient,
  forNonAdvisoryUser,
  isSmallScreen,
}: CheckAuthProps) {
  const isExpiredAuth = isExpiredJWT(jwtToken);
  if (isExpiredAuth) {
    clearUserInfo(apolloClient);
    window.location.reload();
  } else if (!auth.isAuthenticated) {
    const loginQuery = loginRedirectQuery(pathname);
    const loginParams = new URLSearchParams();
    for (const [key, value] of Object.entries(loginQuery)) {
      if (!isNil(value)) loginParams.set(key, value);
    }
    router.push(`/login?${loginParams.toString()}`);
    return;
  } else if (auth.user?.confirmationStatus === USER_CONFIRMATION_STATUS_PENDING_EMAIL_CONFIRMATION) {
    // Already on email conf page, no redirect needed
    if (pathname === '/confirm-email') {
      setChecked(true);
    } else {
      const search = searchParams?.toString();
      const asPath = search ? `${pathname}?${search}` : pathname;
      router.push(`/confirm-email?returnUrl=${encodeURIComponent(asPath)}`);
      return;
    }
  } else if (forClient && auth.user?.profileType === USER_PROFILE_CLIENT && auth.user.clientStage) {
    if (auth.user.role === 'NON_ADVISORY_USER' && !forNonAdvisoryUser) {
      router.push('/portfolio');
      setChecked(true);
      return;
    }
    const stageRedirectRoute = getClientStageRedirectRoute(pathname, auth.user, isSmallScreen);
    if (stageRedirectRoute) {
      router.replace(stageRedirectRoute);
      return;
    }
    setChecked(true);
  } else if (
    forClient &&
    auth.user?.profileType === 'CLIENT' &&
    auth.user.role === 'NON_ADVISORY_USER' &&
    !forNonAdvisoryUser
  ) {
    router.push('/portfolio');
    setChecked(true);
  } else if (forAdvisor && auth.user?.profileType !== 'ADVISOR') {
    if (auth.user?.role === 'NON_ADVISORY_USER' && !forNonAdvisoryUser) {
      router.push('/portfolio');
      return;
    }
    router.push('/dashboard');
    return;
  } else if (
    (forAdmin || forAdminOrCsa) &&
    auth.user?.role !== 'ADMIN' &&
    auth.user?.role !== 'ADMIN_CSA' &&
    auth.user?.role !== 'READ_ONLY_ADMIN' &&
    !(forAdminOrCsa && auth.user?.role === 'CSA')
  ) {
    if (auth.user?.role === 'NON_ADVISORY_USER' && !forNonAdvisoryUser) {
      router.push('/portfolio');
      setChecked(true);
      return;
    }
    router.push('/');
    return;
  } else if (forClient && auth.user?.profileType !== 'CLIENT') {
    router.push('/advisor/dashboard/clients');
    return;
  } else {
    setChecked(true);
  }
}

export function AuthGuard(props: AuthGuardProps) {
  const { children, forAdmin, forAdminOrCsa, forAdvisor, forClient, forNonAdvisoryUser } = props;
  const auth = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const apolloClient: ApolloClient<object> = useApolloClient();
  const [checked, setChecked] = useState(false);
  const jwtToken = window.localStorage.getItem('accessToken');
  const isSmallScreen = useIsSmallScreen();

  useEffect(() => {
    const intervalId = setInterval(() => {
      const isExpiredAuth = isExpiredJWT(jwtToken);
      if (isExpiredAuth) {
        const search = searchParams?.toString();
        const currentPath = pathname ?? '';
        const asPath = search ? `${currentPath}?${search}` : currentPath;
        window.location.href = `/login?returnUrl=${window.encodeURIComponent(asPath)}&reAuth=true`;
      }
    }, 500);

    if (isNil(searchParams)) {
      return () => clearInterval(intervalId);
    }
    checkAuth({
      jwtToken,
      apolloClient,
      auth,
      router,
      pathname: pathname ?? '',
      searchParams,
      setChecked,
      forAdmin,
      forAdminOrCsa,
      forAdvisor,
      forClient,
      forNonAdvisoryUser,
      isSmallScreen,
    });
    return () => clearInterval(intervalId);
  }, [
    apolloClient,
    auth,
    forAdmin,
    forAdminOrCsa,
    forAdvisor,
    forClient,
    forNonAdvisoryUser,
    isSmallScreen,
    jwtToken,
    pathname,
    router,
    searchParams,
  ]);

  if (!checked) {
    return null;
  }

  // If we got here, auth is valid — the user is authenticated for this guard.
  // LaunchDarkly is provided by `LaunchDarklyFlagsWithStatusProvider` in `pages/_app.tsx` (not by AuthGuard).
  return children;
}

AuthGuard.propTypes = {
  children: PropTypes.node,
};
